import React, { createContext, useContext, useMemo } from 'react';
import { createTheme, Theme, ThemeOptions } from '@mui/material';
import { ThemeProvider } from '@mui/material';
import { merge } from 'lodash-es';

import { DeepPartial } from '~/utils/types.ts';
import CommanderTheme, {
  commanderThemeOptions,
} from '../commander-mui-theme/index.ts';
import '../commander-mui-theme/variables.css';

/**
 * @description Common color variants that are applicable to most components with
 * customizable color
 */
export type Color =
  | 'success'
  | 'info'
  | 'warning'
  | 'error'
  | 'primary'
  | 'secondary';

export interface SharedComponentsThemeOptions {
  color: {
    primary?: string;
    secondary?: string;
    success?: string;
    error?: string;
  };
  fontFamily?: string;
  useInstoreSecondaryButtons?: boolean;
  usePrimaryColorForSecondary?: boolean;
}

export interface CommanderProviderProps {
  theme?: SharedComponentsThemeOptions;
  children?: React.ReactNode;
}

const generateMUITheme = ({
  color,
  fontFamily,
  usePrimaryColorForSecondary,
}: SharedComponentsThemeOptions): Theme => {
  const commanderOverrides: DeepPartial<ThemeOptions> = {
    palette: {
      primary: {
        main: color.primary,
      },
      secondary: {
        main: usePrimaryColorForSecondary
          ? color.primary ?? CommanderTheme.palette.primary.main
          : color.secondary,
      },
      success: {
        main: color.success,
      },
      error: {
        main: color.error,
      },
    },
    typography: {
      fontFamily,
    },
  };

  // even though createTheme can take multiple arguments, only the first one is processed into
  // a theme. The rest of the arguments are applied to the theme _after_ its created.
  // For properties like `typography.fontFamily` to apply everywhere (i.e. to all Typography
  // variants) we need to do a manual merge before calling createTheme.
  // https://mui.com/material-ui/customization/theming/#api
  return createTheme(merge({}, commanderThemeOptions, commanderOverrides));
};

export interface CommanderContext {
  useInstoreSecondaryButtons: boolean;
}

const CommanderContext = createContext<CommanderContext>({
  useInstoreSecondaryButtons: true,
});

export const useCommanderContext = (): CommanderContext =>
  useContext(CommanderContext);

export const CommanderProvider: React.FC<CommanderProviderProps> = ({
  theme,
  children,
}) => {
  const themeToUse = useMemo(
    () => (theme ? generateMUITheme(theme) : CommanderTheme),
    [theme],
  );

  const contextValue = useMemo<CommanderContext>(
    () => ({
      useInstoreSecondaryButtons: !!theme?.useInstoreSecondaryButtons,
    }),
    [theme?.useInstoreSecondaryButtons],
  );

  return (
    <CommanderContext.Provider value={contextValue}>
      <ThemeProvider theme={themeToUse}>{children}</ThemeProvider>
    </CommanderContext.Provider>
  );
};
